import React, { lazy, Suspense } from 'react';

interface Opts {
  fallback: React.ReactNode;
}
type Unpromisify<T> = T extends Promise<infer P> ? P : never;

const RELOAD_FLAG = 'chunk-reload';
const isChunkLoadError = (e: unknown): boolean =>
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Failed to load module script/i.test(
    (e as Error)?.message ?? '',
  );

// A new deploy ships fresh content-hashed chunks. A tab still running the
// previous build that lazy-loads a now-missing chunk reloads once to pull the
// new index.html (which references the new chunks). The sessionStorage flag
// prevents an infinite reload loop when the failure is a real bug, not a stale
// chunk — that case falls through to the ErrorBoundary.
const withChunkRetry =
  <T,>(factory: () => Promise<T>) =>
  (): Promise<T> =>
    factory().then(
      mod => {
        sessionStorage.removeItem(RELOAD_FLAG);
        return mod;
      },
      (err: unknown) => {
        if (isChunkLoadError(err) && !sessionStorage.getItem(RELOAD_FLAG)) {
          sessionStorage.setItem(RELOAD_FLAG, '1');
          window.location.reload();
          return new Promise<T>(() => {});
        }
        throw err;
      },
    );

export const lazyLoad = <
  T extends Promise<any>,
  U extends React.ComponentType<any>,
>(
  importFunc: () => T,
  selectorFunc?: (s: Unpromisify<T>) => U,
  opts: Opts = { fallback: null },
): U => {
  let lazyFactory: () => Promise<{ default: U }> = importFunc as any;

  if (selectorFunc) {
    lazyFactory = () =>
      importFunc().then(module => ({ default: selectorFunc(module) }));
  }

  const LazyComponent = lazy(withChunkRetry(lazyFactory));

  const Component = (props: React.ComponentProps<U>): React.JSX.Element => (
    <Suspense fallback={opts.fallback!}>
      <LazyComponent {...props} />
    </Suspense>
  );
  return Component as unknown as U;
};
